import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Play,
  Eye,
  Heart,
  Share2,
  ArrowLeft,
  BadgeCheck,
  MapPin,
  Loader2,
} from "lucide-react";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { locationLabel } from "@/lib/mappers";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo } from "@/components/Logo";
import { LiveBadge } from "@/components/LiveBadge";
import { LiveChatPlaceholder } from "@/components/live/LiveChatPlaceholder";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function LiveDetail() {
  const { id } = useParams();
  const { setTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);

  // Immersive dark mode for the live route; revert on leave.
  useEffect(() => {
    setTheme("dark");
    return () => setTheme("light");
  }, [setTheme]);

  const { data: stream, loading, error, refetch } = useMockQuery(
    () => apiClient.getLive(id),
    [id]
  );

  const handleWatch = async () => {
    if (!user) {
      toast.error("Log in to watch this live", {
        description: "Sign in to join the stream.",
        action: { label: "Log in", onClick: () => navigate("/login") },
      });
      return;
    }
    setJoining(true);
    try {
      const res = await apiClient.watchStream(stream.id);
      if (res?.livekit?.token) {
        toast.success("You're in!", {
          description: "Connected to the live room. Video playback is coming soon.",
        });
      } else {
        toast.info("Stream joined", { description: "Video playback is coming soon." });
      }
    } catch (err) {
      if (err.code === 401) {
        toast.error("Session expired", { description: "Please log in again." });
        navigate("/login");
      } else {
        toast.error("Couldn't join the stream", { description: "It may have ended." });
      }
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1526] text-white">
      {/* Top bar */}
      <div className="sticky top-0 z-50 flex items-center justify-between border-b border-white/10 bg-black/60 px-5 py-3 backdrop-blur-xl sm:px-8">
        <button
          data-testid="live-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <Link to="/" className="flex items-center">
          <Logo size={34} wordmarkColor="text-white" />
        </Link>
      </div>

      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
        {loading ? (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <Skeleton className="aspect-video w-full rounded-2xl bg-white/5" />
            <Skeleton className="h-[70vh] w-full rounded-2xl bg-white/5" />
          </div>
        ) : error ? (
          <div className="py-20">
            <ErrorState
              title="Stream unavailable"
              description="This stream may have ended or the link is invalid."
              error={error}
              onRetry={refetch}
            />
            <div className="mt-6 text-center">
              <Link to="/explore" className="text-sm font-semibold text-brand">
                ← Discover live streams
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            {/* Player + info */}
            <div className="space-y-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black"
              >
                <img
                  src={stream.thumbnail}
                  alt={stream.title}
                  className="h-full w-full object-cover opacity-80"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <button
                    data-testid="live-play-btn"
                    onClick={handleWatch}
                    disabled={joining}
                    className="flex h-20 w-20 items-center justify-center rounded-full bg-brand/90 text-white shadow-2xl transition-transform duration-300 hover:scale-105 disabled:opacity-70"
                    aria-label="Play stream"
                  >
                    {joining ? (
                      <Loader2 className="h-8 w-8 animate-spin" />
                    ) : (
                      <Play className="ml-1 h-8 w-8 fill-white" />
                    )}
                  </button>
                </div>
                <div className="absolute left-4 top-4 flex items-center gap-2">
                  <LiveBadge />
                  <span className="flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold backdrop-blur-md">
                    <Eye className="h-3.5 w-3.5" />
                    {stream.viewers.toLocaleString()}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-3 py-1 text-xs text-neutral-200 backdrop-blur-md">
                  Video playback is a placeholder — LiveKit not connected.
                </div>
              </motion.div>

              <div className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#111f38] p-6 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h1 className="font-heading text-xl font-extrabold tracking-tight sm:text-2xl">
                    {stream.title}
                  </h1>
                  <p className="mt-2 flex items-center gap-2 text-sm text-neutral-400">
                    <MapPin className="h-4 w-4" />
                    {locationLabel(stream.location)} · {stream.category}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button data-testid="live-like-btn" variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
                    <Heart className="mr-2 h-4 w-4" /> Like
                  </Button>
                  <Button data-testid="live-share-btn" variant="outline" className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10">
                    <Share2 className="mr-2 h-4 w-4" /> Share
                  </Button>
                </div>
              </div>

              {/* Host row */}
              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#111f38] p-5">
                <Link
                  to={`/profile/${stream.host.username}`}
                  className="flex items-center gap-3"
                >
                  <Avatar className="h-12 w-12 border-2 border-brand">
                    <AvatarImage src={stream.host.avatar} alt={stream.host.displayName} />
                    <AvatarFallback>{stream.host.displayName[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="flex items-center gap-1 font-semibold">
                      {stream.host.displayName}
                      {stream.host.verified && (
                        <BadgeCheck className="h-4 w-4 text-brand" />
                      )}
                    </p>
                    <p className="text-sm text-neutral-400">@{stream.host.username}</p>
                  </div>
                </Link>
                <Button
                  data-testid="live-follow-btn"
                  className="rounded-full bg-brand hover:bg-brand-hover"
                >
                  Follow
                </Button>
              </div>
            </div>

            {/* Chat */}
            <div className="h-[600px] lg:h-auto">
              <LiveChatPlaceholder eventId={stream.eventId} viewers={stream.viewers} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
